import { useCallback, useEffect, useRef, useState } from 'react';
import { DataService } from '../../services/DataService';
import type { InterimApplication } from '../../types/database';
import { INTERIM_ROUTE_SECTIONS, parsePrettyProjectInterimPath, parsePrettyProjectTabPath, slugifySegment, type InterimRouteSection } from '../../utils/prettyRoutes';

export const PROJECT_INTERIM_ROUTE_CHANGE = 'project-interim:route-change';
export interface ProjectInterimSelection {
  projectId: string;
  applicationId?: string;
  applicationNumber?: number;
  section: InterimRouteSection;
}
export interface ProjectInterimRouteChange { projectId: string; selection: ProjectInterimSelection | null }

/** URL selection is project scoped; an unresolved application never opens a new draft. */
export function useProjectInterimRoute(projectId: string, activeTab: string) {
  const readLocation = useCallback((): ProjectInterimSelection | null | undefined => {
    const route = parsePrettyProjectInterimPath(window.location.pathname);
    const projectRoute = route ?? parsePrettyProjectTabPath(window.location.pathname);
    const project = DataService.getProject(projectId);
    const company = project && DataService.getCompany(project.clientId);
    if (!projectRoute || !project || !company
      || projectRoute.projectSlug !== slugifySegment(project.name)
      || projectRoute.workspaceSlug !== slugifySegment(company.name)) return undefined;
    return route ? { projectId, applicationId: route.applicationId, applicationNumber: route.applicationNumber, section: route.section } : null;
  }, [projectId]);
  const [selection, setSelection] = useState<ProjectInterimSelection | null>(() => readLocation() ?? null);
  const pendingInitialLocation = useRef<string | null>(
    !selection && parsePrettyProjectInterimPath(window.location.pathname) ? window.location.pathname : null,
  );
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const restore = () => {
      const next = readLocation();
      if (next !== undefined) { pendingInitialLocation.current = null; setSelection(next); }
    };
    window.addEventListener('popstate', restore);
    const unsubscribe = DataService.subscribe(() => {
      if (pendingInitialLocation.current === window.location.pathname) restore();
      else pendingInitialLocation.current = null;
      setRevision(value => value + 1);
    });
    return () => { window.removeEventListener('popstate', restore); unsubscribe(); };
  }, [readLocation]);

  const application = selection ? DataService.getInterimApplicationsByProject(projectId).find((row: InterimApplication) => selection.applicationId ? row.id === selection.applicationId : row.applicationNumber === selection.applicationNumber) : undefined;
  const applicationId = application?.id ?? selection?.applicationId;
  const applicationNumber = application?.applicationNumber ?? selection?.applicationNumber;
  useEffect(() => {
    if (pendingInitialLocation.current) return;
    const detail: ProjectInterimRouteChange = {
      projectId,
      selection: activeTab === 'interim' && selection ? { ...selection, applicationId, applicationNumber } : null,
    };
    window.dispatchEvent(new CustomEvent(PROJECT_INTERIM_ROUTE_CHANGE, { detail }));
  }, [activeTab, projectId, selection, applicationId, applicationNumber]);
  useEffect(() => {
    if (activeTab !== 'interim') { pendingInitialLocation.current = null; setSelection(null); }
  }, [activeTab]);

  const open = useCallback((applicationId: string, section = 'summary') => {
    pendingInitialLocation.current = null;
    setSelection({ projectId, applicationId, section: INTERIM_ROUTE_SECTIONS.includes(section as InterimRouteSection) ? section as InterimRouteSection : 'summary' });
  }, [projectId]);
  const close = useCallback(() => { pendingInitialLocation.current = null; setSelection(null); }, []);
  const changeSection = useCallback((section: string) => {
    if (!INTERIM_ROUTE_SECTIONS.includes(section as InterimRouteSection)) return;
    setSelection(current => current && current.section !== section ? { ...current, section: section as InterimRouteSection } : current);
  }, []);
  void revision; // DataService notifications re-check whether the route record has hydrated.
  return { selection: selection ? { ...selection, applicationId, applicationNumber } : null, applicationAvailable: Boolean(application), open, close, changeSection };
}
